import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from '../services/apiService';

// Async thunk for fetching dashboard stats
export const fetchDashboardStats = createAsyncThunk(
  'dashboardStats/fetchStats',
  async ({ force = false } = {}, { rejectWithValue }) => {
    try {
      const response = await apiService.invoices.getDashboardStats(force);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch dashboard stats');
    }
  },
  {
    // Prevent duplicate requests while one is pending
    condition: ({ force } = {}, { getState }) => {
      const { dashboardStats } = getState();
      if (dashboardStats.loading && !force) {
        // Already fetching, don't make duplicate request
        return false;
      }
      return true;
    }
  }
);

const initialState = {
  // Stats data
  draftCount: 0,
  validCount: 0,
  invalidCount: 0,
  cancelledCount: 0,
  submittedCount: 0,
  monthlySubmittedCount: 0,
  submittedCount29Days: 0,
  
  // Loading states
  loading: false,
  error: null,
  lastFetched: null,
  
  // Cache configuration
  cacheValid: false,
  cacheTimestamp: null,
};

const CACHE_DURATION = 30000; // 30 seconds client-side cache

const dashboardStatsSlice = createSlice({
  name: 'dashboardStats',
  initialState,
  reducers: {
    // Update stats from WebSocket or other sources
    updateStats: (state, action) => {
      const stats = action.payload;
      if (stats) {
        state.draftCount = stats.draftCount ?? state.draftCount;
        state.validCount = stats.validCount ?? state.validCount;
        state.invalidCount = stats.invalidCount ?? state.invalidCount;
        state.cancelledCount = stats.cancelledCount ?? state.cancelledCount;
        state.submittedCount = stats.submittedCount ?? state.submittedCount;
        state.monthlySubmittedCount = stats.monthlySubmittedCount ?? state.monthlySubmittedCount;
        state.submittedCount29Days = stats.submittedCount29Days ?? state.submittedCount29Days;
        state.cacheTimestamp = Date.now();
        state.cacheValid = true;
      }
    },
    
    // Invalidate cache to force refresh on next request
    invalidateCache: (state) => {
      state.cacheValid = false;
      state.cacheTimestamp = null;
    },
    
    // Clear error state
    clearError: (state) => {
      state.error = null;
    },
    
    // Reset to initial state and invalidate cache to force refresh on next request
    resetStats: (state) => {
      // Reset all stats to zero
      state.draftCount = 0;
      state.validCount = 0;
      state.invalidCount = 0;
      state.cancelledCount = 0;
      state.submittedCount = 0;
      state.monthlySubmittedCount = 0;
      state.submittedCount29Days = 0;

      // CRITICAL FIX: Mark cache invalid so Dashboard's useEffect triggers a fresh fetch
      // The zeros will show briefly, then Dashboard useEffect sees cacheValid=false and fetches
      state.cacheValid = false;
      state.cacheTimestamp = null;
      state.lastFetched = new Date().toISOString();
      state.error = null;
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDashboardStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDashboardStats.fulfilled, (state, action) => {
        state.loading = false;
        
        if (action.payload.status === 'success' && action.payload.data) {
          const stats = action.payload.data;
          
          // Update all stats
          state.draftCount = stats.draftCount || 0;
          state.validCount = stats.validCount || 0;
          state.invalidCount = stats.invalidCount || 0;
          state.cancelledCount = stats.cancelledCount || 0;
          state.submittedCount = stats.submittedCount || 0;
          state.monthlySubmittedCount = stats.monthlySubmittedCount || 0;
          state.submittedCount29Days = stats.submittedCount29Days || 0;
          
          // Update cache metadata
          state.lastFetched = new Date().toISOString();
          state.cacheTimestamp = Date.now();
          state.cacheValid = true;
          state.error = null;
        }
      })
      .addCase(fetchDashboardStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch dashboard stats';
        state.cacheValid = false;
      });
  },
});

// Selectors
export const selectDashboardStats = (state) => state.dashboardStats;
export const selectStatsLoading = (state) => state.dashboardStats.loading;
export const selectStatsError = (state) => state.dashboardStats.error;
export const selectIsCacheValid = (state) => {
  const { cacheValid, cacheTimestamp } = state.dashboardStats;
  if (!cacheValid || !cacheTimestamp) return false;
  
  // Check if cache is still fresh (within 30 seconds)
  const now = Date.now();
  return (now - cacheTimestamp) < CACHE_DURATION;
};

// Export actions
export const { updateStats, invalidateCache, clearError, resetStats } = dashboardStatsSlice.actions;

export default dashboardStatsSlice.reducer;
